import { POINTS, ROUND_SECONDS, type RoundStats, scoreRound } from "../../shared/score";
import {
  type Contact,
  type FireOrder,
  PipelineState,
  type Point,
  runPipeline,
  SHELL_SPEED,
  type Slots,
  timeToEscape,
} from "./pipeline";

export const WIDTH = 960;
export const HEIGHT = 540;
const GROUND_Y = 500;
const RADAR: Point = { x: 66, y: 472 };
const GUN: Point = { x: 894, y: 470 };

const BEAM_MIN_DEG = 6;
const BEAM_MAX_DEG = 88;
const SWEEP_PERIOD_S = 3.2;
const BEAM_HALF_WIDTH_DEG = 6;
const PAINT_INTERVAL_S = 0.07;

const CANNON_COOLDOWN_S = 0.3;
const ORDER_STALE_S = 0.6;
const BURST_RADIUS = 34;

type PlaneKind = "bomber" | "fighter" | "jet";

const PLANE_SPECS: Record<PlaneKind, { speed: [number, number]; radius: number }> = {
  bomber: { speed: [55, 70], radius: 20 },
  fighter: { speed: [95, 120], radius: 14 },
  jet: { speed: [170, 200], radius: 13 },
};

interface Phase {
  start: number;
  title: string;
  hint: string;
  spawnEvery: number;
  friendlyChance: number;
  kinds: PlaneKind[];
  ghostEvery: number;
}

export const PHASES: Phase[] = [
  {
    start: 0,
    title: "Bombers inbound",
    hint: "Shells take time to fly. A Lead Predictor aims where the bomber will be, not where it was.",
    spawnEvery: 2.8,
    friendlyChance: 0,
    kinds: ["bomber"],
    ghostEvery: 0,
  },
  {
    start: 20,
    title: "Friendlies in the area",
    hint: "Blue aircraft are friendly (−300 each). An IFF Filter keeps the cannon off them.",
    spawnEvery: 2.3,
    friendlyChance: 0.4,
    kinds: ["bomber", "fighter"],
    ghostEvery: 0,
  },
  {
    start: 45,
    title: "Fast movers",
    hint: "Jets punish latency after the predictor. Keep the Lead Predictor next to the cannon.",
    spawnEvery: 2.0,
    friendlyChance: 0.3,
    kinds: ["fighter", "jet"],
    ghostEvery: 0,
  },
  {
    start: 70,
    title: "Jamming detected",
    hint: "Ghost contacts waste shells. Track Confirm drops anything the radar painted only once.",
    spawnEvery: 1.8,
    friendlyChance: 0.3,
    kinds: ["bomber", "fighter", "jet"],
    ghostEvery: 0.8,
  },
];

const PRACTICE_PHASE: Phase = {
  start: 0,
  title: "Practice",
  hint: "Nothing counts yet. Try your pipeline, then press Start round.",
  spawnEvery: 2.4,
  friendlyChance: 0.3,
  kinds: ["bomber", "fighter", "jet"],
  ghostEvery: 2.5,
};

interface Plane {
  id: number;
  kind: PlaneKind;
  friendly: boolean;
  x: number;
  y: number;
  vx: number;
  hits: number;
  lastPaint: number;
  falling: { vy: number; rot: number; spin: number } | null;
}

interface Shell {
  from: Point;
  to: Point;
  launchedAt: number;
  flight: number;
}

interface Burst {
  x: number;
  y: number;
  at: number;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  born: number;
  life: number;
  color: string;
}

interface Popup {
  x: number;
  y: number;
  text: string;
  color: string;
  at: number;
}

interface Blip {
  x: number;
  y: number;
  at: number;
}

export type PipelineNode = number | "radar" | "flak";

export interface GameEvents {
  onPipelineEvent(node: PipelineNode, passed: boolean, reason?: string): void;
  onHud(hud: { remaining: number; score: number; stats: RoundStats; running: boolean }): void;
  onPhase(phase: Phase): void;
  onRoundEnd(stats: RoundStats): void;
}

type Mode = "practice" | "running" | "over";

function emptyStats(): RoundStats {
  return { downed: 0, jetsDowned: 0, friendliesHit: 0, shellsFired: 0, escaped: 0 };
}

const rand = (a: number, b: number) => a + Math.random() * (b - a);
const pick = <T>(xs: T[]): T => xs[Math.floor(Math.random() * xs.length)]!;

export class Game {
  private ctx: CanvasRenderingContext2D;
  private mode: Mode = "practice";
  private time = 0;
  private roundStart = 0;
  private lastFrame = 0;
  private phaseIndex = -1;

  private planes: Plane[] = [];
  private shells: Shell[] = [];
  private bursts: Burst[] = [];
  private particles: Particle[] = [];
  private popups: Popup[] = [];
  private blips: Blip[] = [];
  private pending: FireOrder[] = [];
  private queue: { order: FireOrder; queuedAt: number }[] = [];
  private uiEvents: { at: number; node: PipelineNode; passed: boolean; reason?: string }[] = [];

  private pipeline = new PipelineState();
  private stats = emptyStats();
  private nextId = 1;
  private nextSpawn = 0;
  private nextGhost = 0;
  private nextFire = 0;
  private gunAngle = -Math.PI * 0.75;
  private lastMuzzle = -1;

  private readonly stars = Array.from({ length: 70 }, () => ({
    x: Math.random() * WIDTH,
    y: Math.random() * 300,
    r: Math.random() * 1.2 + 0.2,
    tw: Math.random() * Math.PI * 2,
  }));
  private readonly clouds = Array.from({ length: 7 }, () => ({
    x: Math.random() * WIDTH,
    y: rand(40, 280),
    s: rand(0.6, 1.4),
    v: rand(4, 10),
  }));
  private readonly hills = Array.from({ length: 25 }, (_, i) => ({
    x: (i / 24) * WIDTH,
    y: GROUND_Y - 8 - Math.random() * 22,
  }));

  constructor(
    private canvas: HTMLCanvasElement,
    private getSlots: () => Slots,
    private events: GameEvents,
  ) {
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas 2D is not supported");
    this.ctx = ctx;
    this.resize();
    window.addEventListener("resize", () => this.resize());
    requestAnimationFrame((t) => this.frame(t));
  }

  get running(): boolean {
    return this.mode === "running";
  }

  startRound(): void {
    this.clearField();
    this.mode = "running";
    this.roundStart = this.time;
    this.stats = emptyStats();
    this.phaseIndex = -1;
    this.nextSpawn = this.time + 0.8;
  }

  /** Back to practice after a round ends. */
  practice(): void {
    this.clearField();
    this.mode = "practice";
    this.events.onPhase(PRACTICE_PHASE);
  }

  private clearField(): void {
    this.planes = [];
    this.shells = [];
    this.bursts = [];
    this.popups = [];
    this.blips = [];
    this.pending = [];
    this.queue = [];
    this.uiEvents = [];
    this.pipeline = new PipelineState();
    this.stats = emptyStats();
    this.nextGhost = this.time + 2;
  }

  private resize(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = WIDTH * dpr;
    this.canvas.height = HEIGHT * dpr;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  private frame(now: number): void {
    if (this.canvas.offsetParent === null) {
      // Screen hidden (another map is showing): idle without simulating.
      this.lastFrame = 0;
      requestAnimationFrame((t) => this.frame(t));
      return;
    }
    const dt = this.lastFrame ? Math.min((now - this.lastFrame) / 1000, 0.05) : 0;
    this.lastFrame = now;
    this.update(dt);
    this.render();
    requestAnimationFrame((t) => this.frame(t));
  }

  private currentPhase(): Phase {
    if (this.mode !== "running") return PRACTICE_PHASE;
    const elapsed = this.time - this.roundStart;
    let i = 0;
    while (i + 1 < PHASES.length && PHASES[i + 1]!.start <= elapsed) i++;
    if (i !== this.phaseIndex) {
      this.phaseIndex = i;
      this.events.onPhase(PHASES[i]!);
    }
    return PHASES[i]!;
  }

  private beamAngleDeg(t: number): number {
    const p = (t % SWEEP_PERIOD_S) / SWEEP_PERIOD_S;
    const tri = p < 0.5 ? p * 2 : 2 - p * 2;
    return BEAM_MIN_DEG + (BEAM_MAX_DEG - BEAM_MIN_DEG) * tri;
  }

  private update(dt: number): void {
    if (this.mode === "over") dt = 0;
    this.time += dt;
    const t = this.time;

    for (const c of this.clouds) {
      c.x += c.v * dt;
      if (c.x > WIDTH + 120) c.x = -120;
    }
    if (dt === 0) return;

    const phase = this.currentPhase();

    if (t >= this.nextSpawn) {
      this.spawnPlane(phase);
      this.nextSpawn = t + phase.spawnEvery * rand(0.7, 1.3);
    }
    if (phase.ghostEvery > 0 && t >= this.nextGhost) {
      this.spawnGhost();
      this.nextGhost = t + phase.ghostEvery * rand(0.6, 1.4);
    }

    this.movePlanes(dt);
    this.radarPaint();
    this.deliverOrders();
    this.fireCannon();
    this.detonateShells();
    this.updateParticles(dt);
    this.flushUiEvents();

    if (this.mode === "running") {
      const remaining = ROUND_SECONDS - (t - this.roundStart);
      this.events.onHud({ remaining: Math.max(0, remaining), score: scoreRound(this.stats), stats: this.stats, running: true });
      if (remaining <= 0) {
        this.mode = "over";
        this.events.onRoundEnd({ ...this.stats });
      }
    }
  }

  private spawnPlane(phase: Phase): void {
    const kind = pick(phase.kinds);
    const spec = PLANE_SPECS[kind];
    const dir = Math.random() < 0.5 ? 1 : -1;
    this.planes.push({
      id: this.nextId++,
      kind,
      friendly: Math.random() < phase.friendlyChance,
      x: dir > 0 ? -40 : WIDTH + 40,
      y: rand(48, 250),
      vx: dir * rand(spec.speed[0], spec.speed[1]),
      hits: 0,
      lastPaint: -10,
      falling: null,
    });
  }

  /** A jamming ghost: one radar paint of an aircraft that does not exist. */
  private spawnGhost(): void {
    const contact: Contact = {
      trackId: this.nextId++,
      x: rand(200, WIDTH - 120),
      y: rand(48, 250),
      vx: (Math.random() < 0.5 ? 1 : -1) * rand(60, 180),
      vy: 0,
      friendly: false,
      hits: 1,
      paintedAt: this.time,
    };
    this.blips.push({ x: contact.x, y: contact.y, at: this.time });
    this.ingest(contact);
  }

  private movePlanes(dt: number): void {
    const keep: Plane[] = [];
    for (const p of this.planes) {
      p.x += p.vx * dt;
      if (p.falling) {
        p.falling.vy += 140 * dt;
        p.y += p.falling.vy * dt;
        p.falling.rot += p.falling.spin * dt;
        if (Math.random() < 0.6) this.smoke(p.x, p.y, "rgba(40,40,46,0.7)", 6, 1.4);
        if (p.y >= GROUND_Y - 6) {
          this.explode(p.x, GROUND_Y - 6, 18);
          continue;
        }
        keep.push(p);
        continue;
      }
      if (p.x < -80 || p.x > WIDTH + 80) {
        this.pipeline.forget(p.id);
        if (!p.friendly && this.mode === "running") {
          this.stats.escaped++;
          this.popup(p.vx > 0 ? WIDTH - 40 : 40, p.y, `${POINTS.escaped}`, "#f0a35e");
        }
        continue;
      }
      keep.push(p);
    }
    this.planes = keep;
  }

  private radarPaint(): void {
    const beam = this.beamAngleDeg(this.time);
    for (const p of this.planes) {
      if (p.falling) continue;
      const bearing = (Math.atan2(RADAR.y - p.y, p.x - RADAR.x) * 180) / Math.PI;
      if (Math.abs(bearing - beam) > BEAM_HALF_WIDTH_DEG) continue;
      if (this.time - p.lastPaint < PAINT_INTERVAL_S) continue;
      p.lastPaint = this.time;
      p.hits++;
      this.blips.push({ x: p.x, y: p.y, at: this.time });
      this.ingest({
        trackId: p.id,
        x: p.x,
        y: p.y,
        vx: p.vx,
        vy: 0,
        friendly: p.friendly,
        hits: p.hits,
        paintedAt: this.time,
      });
    }
  }

  private ingest(contact: Contact): void {
    this.uiEvents.push({ at: contact.paintedAt, node: "radar", passed: true });
    const { order, trace } = runPipeline(contact, this.getSlots(), this.pipeline, GUN);
    for (const step of trace) this.uiEvents.push({ at: step.at, node: step.slot, passed: step.passed, reason: step.reason });
    if (order) this.pending.push(order);
  }

  private deliverOrders(): void {
    const still: FireOrder[] = [];
    for (const o of this.pending) {
      if (o.readyAt <= this.time) this.queue.push({ order: o, queuedAt: o.readyAt });
      else still.push(o);
    }
    this.pending = still;
  }

  private fireCannon(): void {
    this.queue = this.queue.filter((q) => this.time - q.queuedAt <= ORDER_STALE_S);
    if (this.time < this.nextFire || this.queue.length === 0) return;

    let index = 0;
    if (this.getSlots().includes("sort")) {
      let best = Infinity;
      this.queue.forEach((q, i) => {
        const tte = timeToEscape(q.order.contact, this.time, WIDTH);
        if (tte < best) {
          best = tte;
          index = i;
        }
      });
    }
    const { order } = this.queue.splice(index, 1)[0]!;
    const aim = order.aim;
    this.gunAngle = Math.atan2(aim.y - GUN.y, aim.x - GUN.x);
    const muzzle = { x: GUN.x + Math.cos(this.gunAngle) * 34, y: GUN.y + Math.sin(this.gunAngle) * 34 };
    this.shells.push({
      from: muzzle,
      to: aim,
      launchedAt: this.time,
      flight: Math.hypot(aim.x - muzzle.x, aim.y - muzzle.y) / SHELL_SPEED,
    });
    this.lastMuzzle = this.time;
    this.nextFire = this.time + CANNON_COOLDOWN_S;
    if (this.mode === "running") this.stats.shellsFired++;
    this.uiEvents.push({ at: this.time, node: "flak", passed: true });
  }

  private detonateShells(): void {
    const flying: Shell[] = [];
    for (const s of this.shells) {
      if (this.time - s.launchedAt < s.flight) {
        flying.push(s);
        continue;
      }
      this.bursts.push({ x: s.to.x, y: s.to.y, at: this.time });
      for (let i = 0; i < 8; i++) this.smoke(s.to.x, s.to.y, "rgba(55,52,50,0.75)", rand(8, 14), rand(0.8, 1.5), 40);
      for (const p of this.planes) {
        if (p.falling) continue;
        if (Math.hypot(p.x - s.to.x, p.y - s.to.y) > BURST_RADIUS + PLANE_SPECS[p.kind].radius) continue;
        this.shootDown(p);
      }
    }
    this.shells = flying;
  }

  private shootDown(p: Plane): void {
    p.falling = { vy: 10, rot: 0, spin: (p.vx > 0 ? 1 : -1) * rand(0.6, 1.4) };
    p.vx *= 0.6;
    this.pipeline.forget(p.id);
    this.explode(p.x, p.y, 10);
    if (this.mode !== "running") {
      this.popup(p.x, p.y - 20, p.friendly ? "friendly!" : "hit", "#9fb3c8");
      return;
    }
    if (p.friendly) {
      this.stats.friendliesHit++;
      this.popup(p.x, p.y - 20, `${POINTS.friendly}`, "#ff5d5d");
    } else {
      this.stats.downed++;
      let pts: number = POINTS.hostile;
      if (p.kind === "jet") {
        this.stats.jetsDowned++;
        pts += POINTS.jetBonus;
      }
      this.popup(p.x, p.y - 20, `+${pts}`, "#7dff9a");
    }
  }

  private explode(x: number, y: number, n: number): void {
    for (let i = 0; i < n; i++) {
      this.particles.push({
        x,
        y,
        vx: rand(-90, 90),
        vy: rand(-110, 30),
        r: rand(2, 5),
        born: this.time,
        life: rand(0.3, 0.7),
        color: pick(["#ffd36b", "#ff8a3d", "#ff5d3d", "#fff3c4"]),
      });
    }
  }

  private smoke(x: number, y: number, color: string, r: number, life: number, spread = 4): void {
    this.particles.push({
      x: x + rand(-spread / 2, spread / 2),
      y: y + rand(-spread / 2, spread / 2),
      vx: rand(-8, 8),
      vy: rand(-14, -2),
      r,
      born: this.time,
      life,
      color,
    });
  }

  private popup(x: number, y: number, text: string, color: string): void {
    this.popups.push({ x, y, text, color, at: this.time });
  }

  private updateParticles(dt: number): void {
    this.particles = this.particles.filter((p) => this.time - p.born < p.life);
    for (const p of this.particles) {
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vy += 40 * dt;
    }
    this.bursts = this.bursts.filter((b) => this.time - b.at < 0.25);
    this.popups = this.popups.filter((p) => this.time - p.at < 1.2);
    this.blips = this.blips.filter((b) => this.time - b.at < 1.6);
  }

  private flushUiEvents(): void {
    if (this.uiEvents.length === 0) return;
    const later: typeof this.uiEvents = [];
    for (const e of this.uiEvents) {
      if (e.at <= this.time) this.events.onPipelineEvent(e.node, e.passed, e.reason);
      else later.push(e);
    }
    this.uiEvents = later;
  }

  // ---------------------------------------------------------------- rendering

  private render(): void {
    const c = this.ctx;
    const t = this.time;

    const sky = c.createLinearGradient(0, 0, 0, GROUND_Y);
    sky.addColorStop(0, "#060d1c");
    sky.addColorStop(0.55, "#12284a");
    sky.addColorStop(1, "#2c4a6e");
    c.fillStyle = sky;
    c.fillRect(0, 0, WIDTH, HEIGHT);

    for (const s of this.stars) {
      c.globalAlpha = 0.4 + 0.4 * Math.sin(t * 1.5 + s.tw);
      c.fillStyle = "#dfe8ff";
      c.fillRect(s.x, s.y, s.r, s.r);
    }
    c.globalAlpha = 1;

    for (const cl of this.clouds) this.drawCloud(cl.x, cl.y, cl.s);

    this.drawBeam();
    for (const b of this.blips) this.drawBlip(b);
    for (const p of this.planes) this.drawPlane(p);
    for (const s of this.shells) this.drawShell(s);
    for (const b of this.bursts) this.drawBurst(b);
    for (const p of this.particles) {
      const age = (t - p.born) / p.life;
      c.globalAlpha = Math.max(0, 1 - age);
      c.fillStyle = p.color;
      c.beginPath();
      c.arc(p.x, p.y, p.r * (1 + age * 0.8), 0, Math.PI * 2);
      c.fill();
    }
    c.globalAlpha = 1;

    this.drawGround();
    this.drawRadar();
    this.drawCannon();

    c.font = "bold 16px ui-monospace, Menlo, monospace";
    c.textAlign = "center";
    for (const p of this.popups) {
      const age = t - p.at;
      c.globalAlpha = Math.max(0, 1 - age / 1.2);
      c.fillStyle = p.color;
      c.fillText(p.text, p.x, p.y - age * 30);
    }
    c.globalAlpha = 1;
  }

  private drawCloud(x: number, y: number, s: number): void {
    const c = this.ctx;
    c.fillStyle = "rgba(160,185,215,0.07)";
    for (const [dx, dy, r] of [
      [0, 0, 26],
      [24, -8, 22],
      [48, 2, 24],
      [-22, 4, 18],
      [70, 6, 16],
    ] as const) {
      c.beginPath();
      c.arc(x + dx * s, y + dy * s, r * s, 0, Math.PI * 2);
      c.fill();
    }
  }

  private drawBeam(): void {
    const c = this.ctx;
    const beam = this.beamAngleDeg(this.time);
    const dir = this.beamAngleDeg(this.time + 0.01) >= beam ? 1 : -1;
    const range = 1150;
    const toRad = (d: number) => (-d * Math.PI) / 180;

    // Fading trail behind the beam.
    for (let k = 0; k < 14; k++) {
      const a = beam - dir * k * 1.6;
      c.fillStyle = `rgba(80,255,140,${0.05 * (1 - k / 14)})`;
      c.beginPath();
      c.moveTo(RADAR.x, RADAR.y);
      c.arc(RADAR.x, RADAR.y, range, toRad(a + 0.8), toRad(a - 0.8), false);
      c.closePath();
      c.fill();
    }
    const g = c.createRadialGradient(RADAR.x, RADAR.y, 10, RADAR.x, RADAR.y, range);
    g.addColorStop(0, "rgba(120,255,170,0.35)");
    g.addColorStop(1, "rgba(120,255,170,0.02)");
    c.fillStyle = g;
    c.beginPath();
    c.moveTo(RADAR.x, RADAR.y);
    c.arc(RADAR.x, RADAR.y, range, toRad(beam + BEAM_HALF_WIDTH_DEG / 3), toRad(beam - BEAM_HALF_WIDTH_DEG / 3), false);
    c.closePath();
    c.fill();
  }

  private drawBlip(b: Blip): void {
    const c = this.ctx;
    const age = (this.time - b.at) / 1.6;
    c.strokeStyle = `rgba(110,255,160,${1 - age})`;
    c.lineWidth = 1.5;
    c.beginPath();
    c.arc(b.x, b.y, 6 + age * 14, 0, Math.PI * 2);
    c.stroke();
    c.fillStyle = `rgba(110,255,160,${0.9 * (1 - age)})`;
    c.beginPath();
    c.arc(b.x, b.y, 2.5, 0, Math.PI * 2);
    c.fill();
  }

  private drawPlane(p: Plane): void {
    const c = this.ctx;
    const body = p.friendly ? "#5d8fd6" : "#c4513f";
    const dark = p.friendly ? "#2f5487" : "#6f2a20";
    c.save();
    c.translate(p.x, p.y);
    if (p.falling) c.rotate(p.falling.rot);
    if (p.vx < 0) c.scale(-1, 1);

    if (p.kind === "bomber") {
      c.fillStyle = dark;
      c.fillRect(-6, 1, 18, 4); // wing
      c.fillStyle = body;
      roundRect(c, -24, -5, 46, 10, 5);
      c.beginPath(); // tail fin
      c.moveTo(-24, -4);
      c.lineTo(-20, -16);
      c.lineTo(-13, -16);
      c.lineTo(-10, -4);
      c.fill();
      c.fillStyle = "#cfe6ff";
      c.fillRect(13, -4, 6, 3); // cockpit
      this.drawProp(c, 23);
    } else if (p.kind === "fighter") {
      c.fillStyle = dark;
      c.fillRect(-4, 0, 12, 3);
      c.fillStyle = body;
      roundRect(c, -15, -3.5, 30, 7, 3.5);
      c.beginPath();
      c.moveTo(-15, -3);
      c.lineTo(-12, -11);
      c.lineTo(-8, -11);
      c.lineTo(-6, -3);
      c.fill();
      c.fillStyle = "#cfe6ff";
      c.beginPath();
      c.ellipse(4, -4, 4, 2.5, 0, Math.PI, 0);
      c.fill();
      this.drawProp(c, 15);
    } else {
      if (!p.falling) {
        c.fillStyle = `rgba(255,${150 + Math.random() * 80},60,0.85)`;
        c.beginPath();
        c.moveTo(-16, -2);
        c.lineTo(-26 - Math.random() * 8, 0);
        c.lineTo(-16, 2);
        c.fill();
      }
      c.fillStyle = body;
      c.beginPath();
      c.moveTo(18, 0);
      c.lineTo(6, -3.5);
      c.lineTo(-16, -3);
      c.lineTo(-16, 3);
      c.lineTo(6, 3.5);
      c.closePath();
      c.fill();
      c.fillStyle = dark;
      c.beginPath();
      c.moveTo(4, 1);
      c.lineTo(-10, 6);
      c.lineTo(-6, 1);
      c.fill();
      c.fillStyle = body;
      c.beginPath();
      c.moveTo(-8, -3);
      c.lineTo(-15, -12);
      c.lineTo(-16, -3);
      c.fill();
      c.fillStyle = "#cfe6ff";
      c.fillRect(4, -4, 5, 1.6);
    }

    if (p.friendly) {
      c.fillStyle = "#ffffff";
      c.beginPath();
      c.arc(-3, 0, 3.4, 0, Math.PI * 2);
      c.fill();
      c.fillStyle = "#2b59a8";
      c.beginPath();
      c.arc(-3, 0, 1.8, 0, Math.PI * 2);
      c.fill();
    }
    c.restore();
  }

  private drawProp(c: CanvasRenderingContext2D, x: number): void {
    c.fillStyle = "rgba(220,220,220,0.45)";
    c.beginPath();
    c.ellipse(x, 0, 1.5, 7 + Math.sin(this.time * 60) * 1.5, 0, 0, Math.PI * 2);
    c.fill();
  }

  private drawShell(s: Shell): void {
    const c = this.ctx;
    const k = Math.min(1, (this.time - s.launchedAt) / s.flight);
    const k0 = Math.max(0, k - 0.06);
    const x = s.from.x + (s.to.x - s.from.x) * k;
    const y = s.from.y + (s.to.y - s.from.y) * k;
    c.strokeStyle = "rgba(255,220,120,0.6)";
    c.lineWidth = 1.5;
    c.beginPath();
    c.moveTo(s.from.x + (s.to.x - s.from.x) * k0, s.from.y + (s.to.y - s.from.y) * k0);
    c.lineTo(x, y);
    c.stroke();
    c.fillStyle = "#fff1b8";
    c.beginPath();
    c.arc(x, y, 2, 0, Math.PI * 2);
    c.fill();
  }

  private drawBurst(b: Burst): void {
    const c = this.ctx;
    const k = (this.time - b.at) / 0.25;
    c.fillStyle = `rgba(255,230,150,${0.8 * (1 - k)})`;
    c.beginPath();
    c.arc(b.x, b.y, BURST_RADIUS * (0.4 + k * 0.6), 0, Math.PI * 2);
    c.fill();
  }

  private drawGround(): void {
    const c = this.ctx;
    c.fillStyle = "#0f1a14";
    c.beginPath();
    c.moveTo(0, HEIGHT);
    for (const h of this.hills) c.lineTo(h.x, h.y);
    c.lineTo(WIDTH, HEIGHT);
    c.closePath();
    c.fill();
    c.fillStyle = "#16261c";
    c.fillRect(0, GROUND_Y, WIDTH, HEIGHT - GROUND_Y);
    c.fillStyle = "#2a4434";
    c.fillRect(0, GROUND_Y, WIDTH, 2);
  }

  private drawRadar(): void {
    const c = this.ctx;
    const beam = (this.beamAngleDeg(this.time) * Math.PI) / 180;
    c.fillStyle = "#3b4a44";
    c.fillRect(RADAR.x - 18, RADAR.y + 6, 36, GROUND_Y - RADAR.y - 6);
    c.fillStyle = "#56685f";
    c.fillRect(RADAR.x - 3, RADAR.y - 2, 6, 10);
    c.save();
    c.translate(RADAR.x, RADAR.y);
    c.rotate(-beam);
    c.strokeStyle = "#b9d6c4";
    c.lineWidth = 4;
    c.beginPath();
    c.arc(-8, 0, 18, -Math.PI / 2.6, Math.PI / 2.6);
    c.stroke();
    c.fillStyle = "#e9fff1";
    c.fillRect(-2, -1.5, 14, 3);
    c.restore();
    c.fillStyle = "#7dff9a";
    c.font = "bold 11px ui-monospace, Menlo, monospace";
    c.textAlign = "center";
    c.fillText("RADAR", RADAR.x, GROUND_Y + 22);
  }

  private drawCannon(): void {
    const c = this.ctx;
    c.save();
    c.translate(GUN.x, GUN.y);
    c.rotate(this.gunAngle);
    c.fillStyle = "#6c7378";
    c.fillRect(0, -4, 34, 8);
    c.fillStyle = "#4b5156";
    c.fillRect(26, -5, 8, 10);
    if (this.time - this.lastMuzzle < 0.07) {
      c.fillStyle = "#ffe08a";
      c.beginPath();
      c.arc(40, 0, 7, 0, Math.PI * 2);
      c.fill();
    }
    c.restore();
    c.fillStyle = "#545b60";
    c.beginPath();
    c.arc(GUN.x, GUN.y, 13, Math.PI, 0);
    c.fill();
    c.fillStyle = "#3e4448";
    c.beginPath();
    c.moveTo(GUN.x - 26, GROUND_Y);
    c.lineTo(GUN.x - 16, GUN.y);
    c.lineTo(GUN.x + 16, GUN.y);
    c.lineTo(GUN.x + 26, GROUND_Y);
    c.fill();
    c.fillStyle = "#ffcf6b";
    c.font = "bold 11px ui-monospace, Menlo, monospace";
    c.textAlign = "center";
    c.fillText("FLAK", GUN.x, GROUND_Y + 22);
  }
}

function roundRect(c: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  c.beginPath();
  c.roundRect(x, y, w, h, r);
  c.fill();
}
